import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MoonIcon, HeartIcon, ShieldIcon, StarIcon, CheckIcon, ArrowRightIcon, LockIcon, MapPinIcon, ZapIcon, GiftIcon, XIcon } from 'lucide-react';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
type ModalType = 'privacy' | 'terms' | null;
const modalContent = {
  privacy: {
    title: 'Política de Privacidad',
    content: [{
      heading: '1. Información que recopilamos',
      text: 'Al adquirir este producto digital, podemos recopilar tu nombre y dirección de correo electrónico únicamente para procesar tu compra y enviarte el acceso al manual.'
    }, {
      heading: '2. Uso de la información',
      text: 'La información recopilada se utiliza exclusivamente para: (a) procesar y entregar tu compra, (b) enviarte comunicaciones relacionadas con el producto adquirido, y (c) mejorar nuestros productos y servicios.'
    }, {
      heading: '3. Almacenamiento y seguridad',
      text: 'Tus datos son almacenados de forma segura y no son compartidos con terceros sin tu consentimiento, salvo que sea requerido por ley o necesario para procesar tu pago a través de plataformas de pago seguras.'
    }, {
      heading: '4. Tus derechos',
      text: 'Tenés derecho a acceder, rectificar o eliminar tus datos personales en cualquier momento. Para ejercer estos derechos, podés contactarnos a través de los canales de atención disponibles.'
    }, {
      heading: '5. Cookies',
      text: 'Este sitio puede utilizar cookies para mejorar tu experiencia de navegación. Podés configurar tu navegador para rechazarlas, aunque esto podría afectar algunas funcionalidades del sitio.'
    }, {
      heading: '6. Cambios en esta política',
      text: 'Nos reservamos el derecho de actualizar esta política en cualquier momento. Los cambios serán publicados en esta página con la fecha de última actualización.'
    }]
  },
  terms: {
    title: 'Términos y Condiciones',
    content: [{
      heading: '1. Aceptación de los términos',
      text: 'Al adquirir "El Manual del Primer Año sin Caos", aceptás los presentes Términos y Condiciones en su totalidad. Si no estás de acuerdo con alguno de estos términos, te pedimos que no realices la compra.'
    }, {
      heading: '2. Producto digital',
      text: 'Este producto es un bien digital entregado en formato PDF. Una vez completada la compra, recibirás acceso inmediato al material. Por tratarse de un producto digital de entrega inmediata, no se aceptan devoluciones ni reembolsos una vez procesada la compra.'
    }, {
      heading: '3. Propiedad intelectual',
      text: 'Todo el contenido de este manual —incluyendo textos, diseños, ilustraciones y recursos descargables— es propiedad exclusiva de sus autores y está protegido por las leyes de propiedad intelectual vigentes en la República Argentina.'
    }, {
      heading: '4. Uso permitido',
      text: 'La licencia otorgada es personal e intransferible. Podés usar el material para tu uso personal. Queda estrictamente prohibida la reproducción, distribución, venta, modificación o cualquier uso comercial del contenido sin autorización expresa por escrito.'
    }, {
      heading: '5. Limitación de responsabilidad',
      text: 'El contenido de este manual tiene carácter informativo y no reemplaza el consejo médico, psicológico o profesional. Ante cualquier duda sobre la salud de tu bebé, consultá siempre con un profesional de la salud.'
    }, {
      heading: '6. Jurisdicción',
      text: 'Estos términos se rigen por las leyes de la República Argentina. Cualquier controversia será sometida a la jurisdicción de los tribunales ordinarios de la Ciudad Autónoma de Buenos Aires.'
    }]
  }
};
function LegalModal({
  type,
  onClose
}: {
  type: ModalType;
  onClose: () => void;
}) {
  if (!type) return null;
  const data = modalContent[type];
  return <AnimatePresence data-id="element-2">
      <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} data-id="element-3">
        {/* Backdrop */}
        <motion.div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} data-id="element-4" />

        {/* Modal */}
        <motion.div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" initial={{
        opacity: 0,
        y: 40,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 40,
        scale: 0.95
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} data-id="element-5">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-6 border-b border-gray-100" data-id="element-6">
            <div className="flex items-center gap-3" data-id="element-7">
              <div className="w-1 h-8 bg-[#6ECBB8] rounded-full" data-id="element-8" />
              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#2C2C2C]" data-id="element-9">
                {data.title}
              </h2>
            </div>
            <button onClick={onClose} className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors" aria-label="Cerrar" data-id="element-10">
              <XIcon className="w-4 h-4 text-[#6B7280]" data-id="element-11" />
            </button>
          </div>

          {/* Scrollable content */}
          <div className="overflow-y-auto px-8 py-6 space-y-6" data-id="element-12">
            {data.content.map((section, i) => <div key={i} data-id="element-13">
                <h3 className="font-['Playfair_Display'] text-base font-semibold text-[#2C2C2C] mb-2" data-id="element-14">
                  {section.heading}
                </h3>
                <p className="text-[#6B7280] text-sm leading-relaxed" data-id="element-15">
                  {section.text}
                </p>
              </div>)}
          </div>

          {/* Footer */}
          <div className="px-8 py-4 border-t border-gray-100 bg-[#F5ECD7]/40" data-id="element-16">
            <p className="text-xs text-[#6B7280] text-center" data-id="element-17">
              © 2024 El Manual del Primer Año sin Caos · Todos los derechos
              reservados
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>;
}
export function LandingPage() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <div className="min-h-screen w-full bg-white" data-id="element-18">
      {/* Mint accent strip */}
      <div className="h-1 w-full bg-[#6ECBB8]" data-id="element-19" />

      {/* Hero Section */}
      <section className="w-full bg-white py-12 md:py-20 px-4 md:px-8" data-id="element-20">
        <div className="max-w-6xl mx-auto" data-id="element-21">
          <div className="flex flex-col-reverse md:flex-row items-center gap-8 md:gap-16" data-id="element-22">
            {/* Text Content */}
            <motion.div className="flex-1 text-center md:text-left" initial="hidden" animate="visible" variants={staggerContainer} data-id="element-23">
              <motion.p variants={fadeInUp} className="text-[#6ECBB8] text-sm uppercase tracking-[0.2em] font-semibold mb-4" data-id="element-24">
                La Guía Definitiva para Madres Argentinas
              </motion.p>
              <motion.h1 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl font-bold text-[#2C2C2C] leading-tight mb-4" data-id="element-25">
                El Manual del Primer Año sin Caos
              </motion.h1>
              <motion.p variants={fadeInUp} className="text-[#6B7280] text-lg md:text-xl mb-6" data-id="element-26">
                Para madres que quieren paz y confianza
              </motion.p>
              <motion.p variants={fadeInUp} className="text-[#6B7280] text-base md:text-lg mb-8 max-w-lg mx-auto md:mx-0" data-id="element-27">
                Todo el mundo te da consejos contradictorios, las noches se
                hacen eternas y la sobrecarga de información te está agotando.
                Este manual es tu mapa directo a las soluciones reales.
              </motion.p>
              <motion.div variants={fadeInUp} data-id="element-28">
                <motion.button whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.98
              }} onClick={() => document.getElementById('oferta')?.scrollIntoView({
                behavior: 'smooth'
              })} className="bg-[#6ECBB8] text-white font-semibold text-lg px-8 py-4 rounded-full inline-flex items-center gap-2 shadow-lg hover:shadow-xl transition-shadow" data-id="element-29">
                  Quiero mi manual ahora
                  <ArrowRightIcon className="w-5 h-5" data-id="element-30" />
                </motion.button>
                <p className="mt-4 text-[#6B7280] text-sm" data-id="element-31">
                  $9.990 ARS · Entrega inmediata
                </p>
              </motion.div>
            </motion.div>

            {/* Product Mockup */}
            <motion.div className="flex-1 flex justify-center" initial={{
            opacity: 0,
            scale: 0.9,
            rotate: 0
          }} animate={{
            opacity: 1,
            scale: 1,
            rotate: -2
          }} transition={{
            duration: 0.6,
            ease: 'easeOut'
          }} data-id="element-32">
              <img src="https://cdn.magicpatterns.com/uploads/eQd19xd3mejZpgkgaAxnz8/Mockup_Ebook.png" alt="El Manual del Primer Año sin Caos - Mockup del ebook" className="w-full max-w-md md:max-w-lg rounded-2xl shadow-2xl" data-id="element-33" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="w-full bg-[#F5ECD7] py-16 md:py-24 px-4 md:px-8" data-id="element-34">
        <div className="max-w-6xl mx-auto" data-id="element-35">
          <motion.div className="text-center mb-12" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} data-id="element-36">
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C2C2C] mb-4" data-id="element-37">
              ¿Te sentís abrumada en el primer año de tu bebé?
            </h2>
            <p className="text-[#6B7280] text-lg" data-id="element-38">
              No estás sola. Esto es lo que viven la mayoría de las mamás...
            </p>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} data-id="element-39">
            <motion.div variants={fadeInUp} className="bg-white rounded-xl p-8 shadow-sm" data-id="element-40">
              <span className="text-4xl mb-4 block" data-id="element-41">🌙</span>
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#2C2C2C] mb-2" data-id="element-42">
                Noches interminables
              </h3>
              <p className="text-[#6B7280]" data-id="element-43">
                Despertares cada hora y no saber cómo volver a dormir
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-white rounded-xl p-8 shadow-sm" data-id="element-44">
              <span className="text-4xl mb-4 block" data-id="element-45">📚</span>
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#2C2C2C] mb-2" data-id="element-46">
                Información contradictoria
              </h3>
              <p className="text-[#6B7280]" data-id="element-47">
                Cada persona te dice algo diferente y no sabés a quién creerle
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-white rounded-xl p-8 shadow-sm" data-id="element-48">
              <span className="text-4xl mb-4 block" data-id="element-49">😔</span>
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#2C2C2C] mb-2" data-id="element-50">
                Perdiste tu identidad
              </h3>
              <p className="text-[#6B7280]" data-id="element-51">
                Sentís que dejaste de ser vos misma desde que llegó el bebé
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="w-full bg-white py-16 md:py-24 px-4 md:px-8" data-id="element-52">
        <div className="max-w-6xl mx-auto" data-id="element-53">
          <motion.div className="text-center mb-12" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} data-id="element-54">
            <p className="text-[#6ECBB8] text-sm uppercase tracking-[0.2em] font-semibold mb-4" data-id="element-55">
              Lo que vas a lograr
            </p>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C2C2C]" data-id="element-56">
              ¿Qué vas a lograr con este manual?
            </h2>
          </motion.div>

          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} data-id="element-57">
            <motion.div variants={fadeInUp} whileHover={{
            y: -8
          }} className="bg-white rounded-xl p-6 border-t-[3px] border-[#6ECBB8] shadow-sm hover:shadow-lg transition-shadow" data-id="element-58">
              <div className="w-12 h-12 bg-[#6ECBB8]/10 rounded-full flex items-center justify-center mb-4" data-id="element-59">
                <MoonIcon className="w-6 h-6 text-[#6ECBB8]" data-id="element-60" />
              </div>
              <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#2C2C2C] mb-2" data-id="element-61">
                Recuperar el sueño
              </h3>
              <p className="text-[#6B7280] text-sm" data-id="element-62">
                Rutinas probadas para que tu bebé y vos vuelvan a dormir bien
                desde los primeros días.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} whileHover={{
            y: -8
          }} className="bg-white rounded-xl p-6 border-t-[3px] border-[#6ECBB8] shadow-sm hover:shadow-lg transition-shadow" data-id="element-63">
              <div className="w-12 h-12 bg-[#6ECBB8]/10 rounded-full flex items-center justify-center mb-4" data-id="element-64">
                <HeartIcon className="w-6 h-6 text-[#6ECBB8]" data-id="element-65" />
              </div>
              <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#2C2C2C] mb-2" data-id="element-66">
                Simplificar la alimentación
              </h3>
              <p className="text-[#6B7280] text-sm" data-id="element-67">
                Guía práctica de lactancia, papillas y BLW adaptada a la
                realidad local argentina.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} whileHover={{
            y: -8
          }} className="bg-white rounded-xl p-6 border-t-[3px] border-[#6ECBB8] shadow-sm hover:shadow-lg transition-shadow" data-id="element-68">
              <div className="w-12 h-12 bg-[#6ECBB8]/10 rounded-full flex items-center justify-center mb-4" data-id="element-69">
                <ShieldIcon className="w-6 h-6 text-[#6ECBB8]" data-id="element-70" />
              </div>
              <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#2C2C2C] mb-2" data-id="element-71">
                Manejar emergencias con calma
              </h3>
              <p className="text-[#6B7280] text-sm" data-id="element-72">
                Botiquín esencial y pasos claros para fiebre, cólicos y más.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} whileHover={{
            y: -8
          }} className="bg-white rounded-xl p-6 border-t-[3px] border-[#6ECBB8] shadow-sm hover:shadow-lg transition-shadow" data-id="element-73">
              <div className="w-12 h-12 bg-[#6ECBB8]/10 rounded-full flex items-center justify-center mb-4" data-id="element-74">
                <StarIcon className="w-6 h-6 text-[#6ECBB8]" data-id="element-75" />
              </div>
              <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#2C2C2C] mb-2" data-id="element-76">
                Recuperar tu identidad
              </h3>
              <p className="text-[#6B7280] text-sm" data-id="element-77">
                Estrategias para equilibrar tu rol de mamá con tu bienestar
                personal.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why Different Section */}
      <section className="w-full bg-[#F5ECD7] py-16 md:py-24 px-4 md:px-8" data-id="element-78">
        <div className="max-w-6xl mx-auto" data-id="element-79">
          <motion.h2 className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C2C2C] text-center mb-12" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} data-id="element-80">
            ¿Por qué este manual es diferente?
          </motion.h2>

          <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-8" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} data-id="element-81">
            <motion.div variants={fadeInUp} className="text-center" data-id="element-82">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm" data-id="element-83">
                <MapPinIcon className="w-8 h-8 text-[#6ECBB8]" data-id="element-84" />
              </div>
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#2C2C2C] mb-2" data-id="element-85">
                Hecho para madres argentinas
              </h3>
              <p className="text-[#6B7280]" data-id="element-86">
                Listas de compras y recursos adaptados al mercado local. Sin
                productos que no conseguís acá.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="text-center" data-id="element-87">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm" data-id="element-88">
                <ZapIcon className="w-8 h-8 text-[#6ECBB8]" data-id="element-89" />
              </div>
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#2C2C2C] mb-2" data-id="element-90">
                Directo al punto
              </h3>
              <p className="text-[#6B7280]" data-id="element-91">
                Un formato de lectura rápida para madres sin tiempo. Vas al
                grano, sin relleno.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="text-center" data-id="element-92">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm" data-id="element-93">
                <GiftIcon className="w-8 h-8 text-[#6ECBB8]" data-id="element-94" />
              </div>
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#2C2C2C] mb-2" data-id="element-95">
                Bonus incluidos
              </h3>
              <p className="text-[#6B7280]" data-id="element-96">
                Planner de rutinas de sueño y checklist descargables para
                empezar hoy mismo.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="w-full bg-white py-16 md:py-24 px-4 md:px-8" data-id="element-97">
        <div className="max-w-2xl mx-auto text-center" data-id="element-98">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} data-id="element-99">
            {/* Stars */}
            <div className="flex justify-center gap-1 mb-6" data-id="element-100">
              {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-6 h-6 text-yellow-400 fill-yellow-400" data-id="element-101" />)}
            </div>

            {/* Quote mark */}
            <div className="text-[#6ECBB8] text-8xl font-serif leading-none mb-4" data-id="element-102">
              "
            </div>

            {/* Quote */}
            <blockquote className="font-['Playfair_Display'] text-xl md:text-2xl italic text-[#2C2C2C] mb-8" data-id="element-103">
              Después de semanas de no dormir, este manual me devolvió la calma.
              Las rutinas de sueño funcionaron desde el día 3 y ahora disfruto
              más cada momento con mi bebé.
            </blockquote>

            {/* Attribution */}
            <div className="flex items-center justify-center gap-3" data-id="element-104">
              <div className="w-12 h-12 bg-[#6ECBB8] rounded-full flex items-center justify-center text-white font-semibold" data-id="element-105">
                LG
              </div>
              <p className="text-[#6B7280] font-medium" data-id="element-106">
                — Laura G., Buenos Aires
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Offer / CTA Section */}
      <section id="oferta" className="w-full bg-[#2C2C2C] py-16 md:py-24 px-4 md:px-8" data-id="element-107">
        <div className="max-w-3xl mx-auto text-center" data-id="element-108">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} data-id="element-109">
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6" data-id="element-110">
              Llevate tu copia digital hoy
            </motion.h2>

            <motion.p variants={fadeInUp} className="text-5xl md:text-6xl font-bold text-white mb-4" data-id="element-111">
              $9.990 ARS
            </motion.p>

            <motion.p variants={fadeInUp} className="text-[#6ECBB8] text-lg mb-8" data-id="element-112">
              Entrega inmediata · Acceso de por vida · Sin complicaciones
            </motion.p>

            <motion.ul variants={fadeInUp} className="flex flex-col sm:flex-row flex-wrap justify-center gap-4 mb-10 text-white" data-id="element-113">
              <li className="flex items-center gap-2" data-id="element-114">
                <CheckIcon className="w-5 h-5 text-[#6ECBB8]" data-id="element-115" />
                Manual completo en PDF
              </li>
              <li className="flex items-center gap-2" data-id="element-116">
                <CheckIcon className="w-5 h-5 text-[#6ECBB8]" data-id="element-117" />
                Planner de rutinas de sueño
              </li>
              <li className="flex items-center gap-2" data-id="element-118">
                <CheckIcon className="w-5 h-5 text-[#6ECBB8]" data-id="element-119" />
                Checklist descargables
              </li>
              <li className="flex items-center gap-2" data-id="element-120">
                <CheckIcon className="w-5 h-5 text-[#6ECBB8]" data-id="element-121" />
                Adaptado al mercado argentino
              </li>
            </motion.ul>

            <motion.div variants={fadeInUp} data-id="element-122">
              <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.98
            }} onClick={() => window.open('https://ebookpromax.com/cart/46538159718558:1', '_blank')} animate={{
              boxShadow: ['0 0 0 0 rgba(110, 203, 184, 0.4)', '0 0 0 10px rgba(110, 203, 184, 0)', '0 0 0 0 rgba(110, 203, 184, 0)']
            }} transition={{
              boxShadow: {
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut'
              }
            }} className="bg-[#6ECBB8] text-white font-semibold text-xl px-10 py-5 rounded-full inline-flex items-center gap-2 shadow-lg hover:shadow-xl transition-shadow" data-id="element-123">
                Quiero mi manual ahora
                <ArrowRightIcon className="w-6 h-6" data-id="element-124" />
              </motion.button>

              <p className="mt-6 text-[#6B7280] text-sm flex items-center justify-center gap-2" data-id="element-125">
                <LockIcon className="w-4 h-4" data-id="element-126" />
                Pago seguro · Satisfacción garantizada
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full bg-white" data-id="element-127">
        <div className="h-1 w-full bg-[#6ECBB8]" data-id="element-128" />
        <div className="py-8 px-4 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8" data-id="element-129">
          <p className="text-[#6B7280] text-sm" data-id="element-130">
            © 2024 El Manual del Primer Año sin Caos · Todos los derechos
            reservados
          </p>
          <div className="flex items-center gap-6" data-id="element-131">
            <button onClick={() => setActiveModal('privacy')} className="text-[#6B7280] text-sm hover:text-[#6ECBB8] transition-colors underline underline-offset-2" data-id="element-132">
              Política de Privacidad
            </button>
            <button onClick={() => setActiveModal('terms')} className="text-[#6B7280] text-sm hover:text-[#6ECBB8] transition-colors underline underline-offset-2" data-id="element-133">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-134" />
    </div>;
}